import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { AppActions } from "../../../../store/ducks/app";
import { AtribuirActions } from "../../../../store/ducks/atribuir";
import { AuthActions } from "../../../../store/ducks/auth";
import { DashBoardActions } from "../../../../store/ducks/dashboard";
import { RelatorioActions } from "../../../../store/ducks/relatorios";
import { getPainQuestionnaire } from "../../../../services/painQuestionnaire";
import { handlePaciente } from "../../../../utils/funcs";
import { CopilotSummarySnapshot } from "../AICopilotSection/useAICopilotViewModel";
import {
	AttentionPointLevel,
	CopilotAttentionPoint,
	CopilotPatientReference,
	CopilotSummaryData,
	fetchCopilotSummary,
	fetchFullPatientForDashboard,
	fetchPatientRecordSummary,
	isAbortError,
} from "../../services/clinicalDashboardService";

interface AttentionState {
	patientId: number | null;
	data: CopilotSummaryData | null;
	loading: boolean;
	error: string | null;
}

const emptyAttention = (patientId: number | null = null): AttentionState => ({
	patientId,
	data: null,
	loading: false,
	error: null,
});

const attentionMessage = (point?: CopilotAttentionPoint | null) => {
	if (!point?.status) {
		return {
			status: "INDISPONIVEL" as const,
			level: null,
			text: "Informação ainda não disponível",
		};
	}
	if (point.status === "SEM_ALERTA") {
		return {
			status: point.status,
			level: null,
			text: "Nenhum ponto de atenção identificado",
		};
	}
	if (point.status === "DADOS_INSUFICIENTES") {
		return {
			status: point.status,
			level: null,
			text:
				point.texto || "Dados insuficientes para identificar pontos de atenção",
		};
	}
	return {
		status: point.status,
		level: (point.nivel || null) as AttentionPointLevel | null,
		text: point.texto || "Informação ainda não disponível",
	};
};

export const useDetailedRecordModalViewModel = (
	isOpen: boolean,
	patient: CopilotPatientReference | null,
	copilotSummary: CopilotSummarySnapshot | null
) => {
	const dispatch = useDispatch();
	const token = useSelector((state: any) => state?.auth?.user?.token) || "";
	const [record, setRecord] = useState<any>(null);
	const [recordLoading, setRecordLoading] = useState(false);
	const [recordError, setRecordError] = useState<string | null>(null);
	const [attention, setAttention] = useState<AttentionState>(emptyAttention());
	const recordRequest = useRef<AbortController | null>(null);
	const attentionRequest = useRef<AbortController | null>(null);
	const recordSequence = useRef(0);
	const attentionSequence = useRef(0);

	const configServices = useSelector(
		(state: any) => state?.configs?.config_servicos || []
	);
	const language = useSelector((state: any) => state?.configs?.language);
	const currentLanguage = useSelector(
		(state: any) => state?.configs?.current_language
	);
	const painMapRequest = useRef<AbortController | null>(null);

	const handleViewMapPain = useCallback(async () => {
		if (!patient?.id || !token) return;
		painMapRequest.current?.abort();
		const controller = new AbortController();
		painMapRequest.current = controller;
		try {
			const fullPatient = await fetchFullPatientForDashboard(
				token,
				patient.id,
				controller.signal
			);
			if (!fullPatient || controller.signal.aborted) return;
			dispatch(RelatorioActions.setQuestionnairePainEVA([] as any));
			await getPainQuestionnaire(
				token,
				fullPatient.ID_USUARIO,
				language,
				dispatch,
				controller.signal
			);
			if (controller.signal.aborted) return;
			handlePaciente(fullPatient, dispatch, configServices);
			dispatch(AtribuirActions.set_menu_option_selected(""));
			dispatch(AppActions.set_lista_pacientes([]));
			dispatch(
				AppActions.set_option_menu(false, currentLanguage?.menu_lateral_inicio)
			);
			dispatch(DashBoardActions.set_current_page("mapa_dor"));
		} catch (requestError) {
			if (
				!isAbortError(requestError) &&
				(requestError as any)?.status === 401
			) {
				dispatch(AuthActions.auth_logout());
			}
		} finally {
			if (painMapRequest.current === controller) {
				painMapRequest.current = null;
			}
		}
	}, [
		patient?.id,
		token,
		dispatch,
		configServices,
		currentLanguage?.menu_lateral_inicio,
		language,
	]);

	useEffect(() => {
		return () => {
			painMapRequest.current?.abort();
			painMapRequest.current = null;
		};
	}, [isOpen, patient?.id]);

	const loadRecord = useCallback(async () => {
		if (!patient?.id || !token) return;
		const patientId = patient.id;
		recordRequest.current?.abort();
		const controller = new AbortController();
		recordRequest.current = controller;
		const sequence = ++recordSequence.current;
		setRecordLoading(true);
		setRecordError(null);
		try {
			const result = await fetchPatientRecordSummary(
				token,
				patientId,
				controller.signal
			);
			if (sequence === recordSequence.current && patientId === patient.id) {
				setRecord(result);
			}
		} catch (requestError) {
			if (!isAbortError(requestError) && sequence === recordSequence.current) {
				if ((requestError as any)?.status === 401) {
					dispatch(AuthActions.auth_logout());
				}
				setRecordError(
					(requestError as Error)?.message ||
						"Não foi possível carregar o prontuário."
				);
				setRecord(null);
			}
		} finally {
			if (sequence === recordSequence.current) setRecordLoading(false);
		}
	}, [patient?.id, token, dispatch]);

	const loadAttention = useCallback(async () => {
		if (!patient?.id || !token) return;
		const patientId = patient.id;
		attentionRequest.current?.abort();
		const controller = new AbortController();
		attentionRequest.current = controller;
		const sequence = ++attentionSequence.current;
		setAttention({ patientId, data: null, loading: true, error: null });
		try {
			const data = await fetchCopilotSummary(
				token,
				patientId,
				controller.signal
			);
			if (sequence === attentionSequence.current && patientId === patient.id) {
				setAttention({ patientId, data, loading: false, error: null });
			}
		} catch (requestError) {
			if (
				!isAbortError(requestError) &&
				sequence === attentionSequence.current
			) {
				if ((requestError as any)?.status === 401) {
					dispatch(AuthActions.auth_logout());
				}
				setAttention({
					patientId,
					data: null,
					loading: false,
					error:
						(requestError as Error)?.message ||
						"Não foi possível carregar os pontos de atenção.",
				});
			}
		}
	}, [patient?.id, token, dispatch]);

	useEffect(() => {
		if (!isOpen) {
			recordSequence.current += 1;
			recordRequest.current?.abort();
			return;
		}
		setRecord(null);
		setRecordError(null);
		loadRecord();
		return () => {
			recordSequence.current += 1;
			recordRequest.current?.abort();
		};
	}, [isOpen, patient?.id, loadRecord]);

	useEffect(() => {
		if (!isOpen || !patient?.id) {
			attentionSequence.current += 1;
			attentionRequest.current?.abort();
			return;
		}

		const sharedMatches = copilotSummary?.patientId === patient.id;
		const sharedHasState =
			sharedMatches &&
			(copilotSummary.loading ||
				copilotSummary.data !== null ||
				copilotSummary.error !== null);

		if (sharedHasState && copilotSummary) {
			attentionSequence.current += 1;
			attentionRequest.current?.abort();
			setAttention({
				patientId: patient.id,
				data: copilotSummary.data,
				loading: copilotSummary.loading,
				error: copilotSummary.error,
			});
			return;
		}

		setAttention(emptyAttention(patient.id));
		loadAttention();
		return () => {
			attentionSequence.current += 1;
			attentionRequest.current?.abort();
		};
	}, [
		isOpen,
		patient?.id,
		copilotSummary?.patientId,
		copilotSummary?.data,
		copilotSummary?.loading,
		copilotSummary?.error,
		loadAttention,
	]);

	const checkIfValueExists = (value: string | null) => {
		if (value !== "Não informado" && value !== null && value !== undefined) {
			return value;
		}
		return "-";
	};

	const view = useMemo(() => {
		if (!record) return null;
		const profile = record.paciente;
		const profileParts = [
			profile?.sexo?.descricao,
			profile?.idade != null ? `${profile.idade} anos` : null,
			profile?.imc != null ? `IMC ${profile.imc}` : null,
			profile?.condicionamento?.descricao,
		].filter(Boolean);

		let pain = "Dados de dor indisponíveis";
		if (record.dor?.status === "NAO_AVALIADA") pain = "Dor ainda não avaliada";
		if (record.dor?.status === "AVALIADA_SEM_DOR") pain = "Sem dor registrada";
		if (record.dor?.status === "DADOS_INSUFICIENTES") {
			pain = record.dor?.texto || "Dados de dor indisponíveis";
		}
		if (record.dor?.status === "AVALIADA_COM_DOR") {
			pain = `Nível ${record.dor.intensidade}`;
			const painDetails = [record.dor.local, record.dor.lado?.descricao].filter(
				Boolean
			);
			if (painDetails.length) pain += ` (${painDetails.join(" ")})`;
		}

		const activity = record.ultimaAtividade;
		const hasActivity = activity?.status === "REALIZADA";
		const activityType =
			activity?.tipo === "EXERCICIO" ? "exercício" : "protocolo";

		return {
			name: profile?.nome || patient?.nome || "Paciente",
			profile: profileParts.length
				? `${profileParts.join(", ")}.`
				: "Perfil não informado",
			upperDominance: checkIfValueExists(
				profile?.dominanciaLateral?.membrosSuperiores?.descricao
			),
			lowerDominance: checkIfValueExists(
				profile?.dominanciaLateral?.membrosInferiores?.descricao
			),
			pain,
			painStatus: record.dor?.status || "DADOS_INSUFICIENTES",
			painDate: record.dor?.avaliadaEm
				? moment(record.dor.avaliadaEm).format("DD/MM/YYYY")
				: null,
			activityLabel: `Último ${activityType}`,
			activity: hasActivity
				? activity.titulo || "Atividade sem título"
				: "Nenhuma atividade concluída",
			activityDate: activity?.realizadaEm
				? moment(activity.realizadaEm).format("DD/MM/YYYY")
				: null,
		};
	}, [record, patient?.nome]);

	const attentionView = useMemo(
		() => attentionMessage(attention.data?.PONTO_ATENCAO),
		[attention.data]
	);

	const retryAttention = () => {
		const sharedRetry = copilotSummary?.retry;
		if (copilotSummary?.patientId === patient?.id && sharedRetry) {
			sharedRetry();
			return;
		}
		loadAttention();
	};

	return {
		view,
		recordLoading,
		recordError,
		attentionView,
		attentionLoading: attention.loading,
		attentionError: attention.error,
		retryRecord: loadRecord,
		retryAttention,
		handleViewMapPain,
	};
};
